import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { useEaDetails, useEaHistory, useResetMaxFloat, REFRESH_INTERVAL_MS } from "../hooks/useDashboardData";
import { StatCard } from "../components/StatCard";
import { StatusBadge } from "../components/StatusBadge";
import { CardSkeleton } from "../components/Skeleton";
import { RefreshCountdown } from "../components/RefreshCountdown";
import { ProfitTodayChart, FloatingPnLChart, EquityHistoryChart } from "../components/HistoryCharts";
import { formatCurrency, formatDateTime, formatNumber } from "../lib/format";
import type { Trade } from "../types";

export function EADetails() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isFetching, dataUpdatedAt } = useEaDetails(id);
  const { data: historyData } = useEaHistory(id);
  const resetMaxFloat = useResetMaxFloat();

  const history = historyData?.history ?? [];

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  const { ea, stats } = data;

  const handleResetMaxFloat = () => {
    const confirmed = window.confirm(
      `Reset the all-time max float for ${ea.account_name || ea.ea_name}? This can't be undone.`,
    );
    if (confirmed) resetMaxFloat.mutate(ea.id);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/eas"
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to EA List
        </Link>
        <RefreshCountdown
          dataUpdatedAt={dataUpdatedAt}
          intervalMs={REFRESH_INTERVAL_MS}
          isFetching={isFetching}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-100">{ea.account_name || ea.ea_name}</h2>
            <StatusBadge status={ea.status} />
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {ea.account_number} ({ea.ea_name}) &middot; {ea.broker} &middot; {ea.symbol}
          </p>
        </div>
        <div className="text-right text-sm text-slate-500">
          Last heartbeat
          <div className="text-slate-300">{formatDateTime(ea.last_heartbeat)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          label="Profit Today"
          value={formatCurrency(ea.today_profit)}
          tone={ea.today_profit >= 0 ? "positive" : "negative"}
        />
        <StatCard
          label="Profit Yesterday"
          value={formatCurrency(ea.yesterday_profit)}
          tone={ea.yesterday_profit >= 0 ? "positive" : "negative"}
        />
        <StatCard
          label="Floating"
          value={formatCurrency(ea.floating_profit - ea.floating_loss)}
          tone={ea.floating_profit - ea.floating_loss >= 0 ? "positive" : "negative"}
        />
        <StatCard
          label="Daily Max Float"
          value={formatCurrency(ea.daily_max_float)}
          tone={ea.daily_max_float < 0 ? "negative" : "default"}
        />
        <StatCard
          label="All-Time Max Float"
          value={ea.all_time_max_float != null ? formatCurrency(ea.all_time_max_float) : "-"}
          subtext={ea.all_time_max_float_at ? formatDateTime(ea.all_time_max_float_at) : undefined}
          tone={ea.all_time_max_float != null && ea.all_time_max_float < 0 ? "negative" : "default"}
          action={
            <button
              type="button"
              onClick={handleResetMaxFloat}
              disabled={resetMaxFloat.isPending || ea.all_time_max_float == null}
              title="Reset all-time max float"
              aria-label="Reset all-time max float"
              className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw className={`h-4 w-4 ${resetMaxFloat.isPending ? "animate-spin" : ""}`} />
            </button>
          }
        />
        <StatCard label="Largest Lot Today" value={formatNumber(ea.largest_lot_today)} />
        <StatCard label="Total Trades Today" value={String(stats.totalTradesToday)} />
        <StatCard label="Winning Trades" value={String(stats.winningTrades)} tone="positive" />
        <StatCard label="Losing Trades" value={String(stats.losingTrades)} tone="negative" />
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label="Win Rate" value={`${formatNumber(stats.winRate, 1)}%`} />
        <StatCard label="Total Lots Today" value={formatNumber(stats.totalLotsToday)} />
        <StatCard label="Balance" value={formatCurrency(ea.balance)} />
        <StatCard label="Equity" value={formatCurrency(ea.equity)} />
        <StatCard
          label="Margin Level"
          value={`${formatNumber(ea.margin_level, 1)}%`}
          tone={ea.margin_level < 150 ? "warning" : "default"}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard title="Profit Today">
          <ProfitTodayChart history={history} />
        </ChartCard>
        <ChartCard title="Floating PnL">
          <FloatingPnLChart history={history} />
        </ChartCard>
        <ChartCard title="Equity History">
          <EquityHistoryChart history={history} />
        </ChartCard>
      </div>

      <RecentTradesTable trades={data.recentTrades} />
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-300">{title}</h3>
      {children}
    </div>
  );
}

function RecentTradesTable({ trades }: { trades: Trade[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900/60">
      <div className="border-b border-slate-800 px-5 py-4 text-sm font-semibold text-slate-100">
        Recent Trades
      </div>
      <table className="w-full min-w-[700px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-800 text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-3">Symbol</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Lots</th>
            <th className="px-4 py-3">Open Price</th>
            <th className="px-4 py-3">Close Price</th>
            <th className="px-4 py-3">Profit</th>
            <th className="px-4 py-3">Closed At</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/70">
          {trades.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                No trades reported yet.
              </td>
            </tr>
          ) : (
            trades.map((t) => (
              <tr key={t.id}>
                <td className="px-4 py-3 text-slate-200">{t.symbol}</td>
                <td className="px-4 py-3 uppercase text-slate-400">{t.trade_type}</td>
                <td className="px-4 py-3 tabular-nums text-slate-300">{formatNumber(t.lot_size)}</td>
                <td className="px-4 py-3 tabular-nums text-slate-300">
                  {t.open_price ? formatNumber(t.open_price, 5) : "-"}
                </td>
                <td className="px-4 py-3 tabular-nums text-slate-300">
                  {t.close_price ? formatNumber(t.close_price, 5) : "-"}
                </td>
                <td
                  className={`px-4 py-3 tabular-nums ${t.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}
                >
                  {formatCurrency(t.profit)}
                </td>
                <td className="px-4 py-3 text-slate-500">{formatDateTime(t.close_time)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
